/*
  Lucía de Miguel · Encaje de imágenes en marcos 9:16.
  - Fotos verticales (ancho/alto ≤ 0,85): ocupan el marco; se conserva la
    altura completa (cabeza y zapatos) y solo se ajustan los laterales.
  - Fotos cuadradas o apaisadas, y fotos de producto sin modelo marcadas por
    el tema (data-vrs-fit-force): se muestran enteras sobre el color de fondo
    de la propia foto.
  Usa los atributos width/height que Shopify ya escribe en cada <img>, así
  que no espera a la descarga ni provoca saltos. No toca el zoom.
*/
(() => {
  const SELECTOR = '.card-gallery img, .vrs-card__media img, media-gallery .product-media img';
  const LIMIT = 0.85;

  const fit = (img) => {
    if (img.closest('.dialog-zoomed-gallery')) return;
    const w = Number(img.getAttribute('width')) || img.naturalWidth;
    const h = Number(img.getAttribute('height')) || img.naturalHeight;
    if (!w || !h) return;
    // 2026-10-17 (claude 2.0, decisión de Rubén): toda foto LLENA el marco, sin franjas.
    // Se prefiere ampliar a dejar bordes. Verticales ancladas arriba (cabeza y prenda);
    // cuadradas o apaisadas, al centro (la pieza suele estar centrada). El zoom sigue mostrando la foto entera.
    const mode = 'cover';
    const shape = w / h > LIMIT ? 'wide' : 'tall';
    if (img.dataset.vrsFit !== mode) img.dataset.vrsFit = mode;
    if (img.dataset.vrsShape !== shape) img.dataset.vrsShape = shape;
  };

  // Foto entera en marco 9:16: las franjas de arriba y abajo prolongan la fila
  // superior e inferior de la propia foto (su fondo de estudio), así no se
  // distinguen. Si esa fila no es fondo liso (la prenda toca el borde), la
  // franja usa un degradado entre los colores de fondo de sus dos esquinas.
  // La foto no se recorta ni se deforma.
  const STRIP = 64;
  const tinted = new Map();
  // Degradado suave de 8 tramos promediados sobre 3 filas del borde (sin rayas
  // del ruido de la foto). Si el borde no es fondo liso, solo las dos esquinas.
  const SEGMENTS = 8;
  const rowStrip = (d, w, h, top) => {
    const rows = top ? [0, 1, 2] : [h - 3, h - 2, h - 1];
    const px = (x) => {
      let r = 0, g = 0, b = 0;
      for (const y of rows) {
        const k = (y * w + x) * 4;
        r += d[k]; g += d[k + 1]; b += d[k + 2];
      }
      return [r / 3, g / 3, b / 3];
    };
    let sum = 0, sum2 = 0;
    for (let x = 0; x < w; x++) {
      const [r, g, b] = px(x);
      const l = 0.299 * r + 0.587 * g + 0.114 * b;
      sum += l;
      sum2 += l * l;
    }
    const sd = Math.sqrt(Math.max(0, sum2 / w - (sum / w) ** 2));
    const avg = (from, to) => {
      let r = 0, g = 0, b = 0;
      for (let x = from; x < to; x++) {
        const c = px(x);
        r += c[0]; g += c[1]; b += c[2];
      }
      const n = to - from;
      return `rgb(${Math.round(r / n)} ${Math.round(g / n)} ${Math.round(b / n)})`;
    };
    if (sd > 14) return `linear-gradient(to right, ${avg(0, 3)}, ${avg(w - 3, w)})`;
    const step = w / SEGMENTS;
    const stops = [];
    for (let i = 0; i < SEGMENTS; i++) {
      stops.push(`${avg(Math.round(i * step), Math.round((i + 1) * step))} ${Math.round(((i + 0.5) / SEGMENTS) * 100)}%`);
    }
    return `linear-gradient(to right, ${stops.join(', ')})`;
  };
  const tint = (img) => {
    const holder = img.closest('.vrs-card__media, .card-gallery .product-media, .vrs-ct__media');
    const src = img.currentSrc || img.getAttribute('src');
    if (!holder || !src || holder.dataset.vrsTint) return;
    holder.dataset.vrsTint = 'pending';
    const url = new URL(src, location.href);
    url.searchParams.set('width', String(STRIP));
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    const apply = (strips) => {
      if (strips && strips.top && strips.bottom) {
        holder.style.backgroundImage = `${strips.top}, ${strips.bottom}`;
        holder.style.backgroundSize = '100% 50%, 100% 50%';
        holder.style.backgroundPosition = 'top, bottom';
        holder.style.backgroundRepeat = 'no-repeat';
        holder.dataset.vrsTint = 'done';
      } else {
        holder.dataset.vrsTint = 'none';
      }
    };
    if (tinted.has(url.href)) return apply(tinted.get(url.href));
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = () => {
      let strips = null;
      try {
        const w = probe.naturalWidth;
        const h = probe.naturalHeight;
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0);
        const d = ctx.getImageData(0, 0, w, h).data;
        strips = { top: rowStrip(d, w, h, true), bottom: rowStrip(d, w, h, false) };
      } catch (e) {}
      tinted.set(url.href, strips);
      apply(strips);
    };
    probe.onerror = () => apply(null);
    probe.src = url.href;
  };

  const scan = (root) => {
    if (!(root instanceof Element || root instanceof Document)) return;
    if (root instanceof HTMLImageElement) return fit(root);
    root.querySelectorAll(SELECTOR).forEach(fit);
  };

  const start = () => {
    scan(document);
    new MutationObserver((records) => {
      for (const r of records) r.addedNodes.forEach((n) => n.nodeType === 1 && scan(n));
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('load', (e) => e.target instanceof HTMLImageElement && e.target.matches(SELECTOR) && fit(e.target), true);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/*
  Lucía de Miguel · Mensajes de promociones con fecha de fin (data-vrs-until="AAAA-MM-DD").
  Se retiran al terminar ese día en hora de Madrid, aunque la página venga de
  caché, para que acaben a la vez que el descuento.
*/
(() => {
  const madridToday = () => {
    try {
      return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    } catch (e) {
      return new Date().toISOString().slice(0, 10);
    }
  };
  const sweep = () => {
    const today = madridToday();
    document.querySelectorAll('[data-vrs-until]').forEach((el) => {
      if (today > el.dataset.vrsUntil) {
        const list = el.parentElement;
        el.remove();
        if (list && !list.children.length) list.closest('.vrs-promos')?.remove();
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  else sweep();
  setInterval(sweep, 60000);
})();
